<script lang="ts" setup>
import type { MaterialSymbolName } from '@shared/types/materialSymbolNames';
import type { IconColor, IconSize } from './BaseIcon.vue';

export type LabelSize = 'small' | 'medium' | 'large';
export type LabelColor =
  | 'neutral'
  | 'primary'
  | 'error'
  | 'warning'
  | 'info'
  | 'community'
  | 'data-highest'
  | 'data-high'
  | 'data-medium'
  | 'data-low';

export type BaseLabelProps = {
  color?: LabelColor;
  size?: LabelSize;
  closeable?: boolean;
  filled?: boolean;
  icon?: MaterialSymbolName;
  wrap?: boolean;
  truncate?: boolean;
};

const props = withDefaults(defineProps<BaseLabelProps>(), {
  color: 'neutral',
  size: 'small',
  filled: true,
  wrap: false,
  truncate: false,
});

const emit = defineEmits<{
  close: [];
}>();

// BaseIcon has no data colours.
const iconColor = computed<IconColor>(() =>
  props.color.startsWith('data-') ? 'inherit' : (props.color as IconColor),
);

// The label's size scale maps onto the icon scale: 16px, 18px, 20px.
const iconSize = computed<IconSize>(() => {
  if (props.size === 'small') return 'sm';
  if (props.size === 'large') return 'lg';
  return 'md';
});
</script>

<template>
  <span
    class="label border-box inline-flex items-center gap-1x rounded-sm border py-1x leading-[125%]"
    :class="[
      color,
      size,
      wrap ? 'break-words whitespace-normal' : 'whitespace-nowrap',
      {
        'text-sm': size === 'small',
        'text-base': size === 'medium',
        'text-md': size === 'large',
        'has-icon': icon,
        filled,
        closeable,
      },
    ]"
  >
    <BaseIcon v-if="icon" :size="iconSize" :name="icon" :color="iconColor" />
    <span class="flex flex-1 items-center font-medium" :class="{ 'min-w-0': truncate }">
      <span v-if="truncate" class="min-w-0 truncate"><slot /></span>
      <slot v-else />
    </span>
    <button
      v-if="closeable"
      class="unstyled-button relative flex items-center text-neutral-muted"
      @click="emit('close')"
    >
      <BaseIcon :size="iconSize" name="close" class="leading-none" data-testid="label-close" />
      <div class="absolute inset-0 -mx-2x -my-1x"></div>
    </button>
  </span>
</template>

<style scoped>
.label {
  /* padding by size; a leading icon or a close button takes one step off its own side */
  &.small {
    padding-inline: var(--spacing-1\.5x);

    &.has-icon {
      padding-left: var(--spacing-1x);
    }

    &.closeable {
      padding-right: var(--spacing-1x);
    }
  }

  &.medium,
  &.large {
    padding-inline: var(--spacing-2x);

    &.has-icon {
      padding-left: var(--spacing-1\.5x);
    }

    &.closeable {
      padding-right: var(--spacing-1\.5x);
    }
  }

  &.neutral {
    color: var(--text-color-neutral);
    border-color: var(--border-color-neutral);

    &.filled {
      background-color: var(--background-color-neutral);
    }

    &.closeable:hover {
      background-color: var(--background-color-neutral-hover);
    }
  }

  &.primary {
    color: var(--text-color-primary);
    border-color: var(--border-color-primary);

    &.filled {
      background-color: var(--background-color-primary);
    }

    &.closeable:hover {
      background-color: var(--background-color-primary-hover);
    }
  }

  &.error {
    color: var(--text-color-error);
    border-color: var(--border-color-error);

    &.filled {
      background-color: var(--background-color-error);
    }

    &.closeable:hover {
      background-color: var(--background-color-error-hover);
    }
  }

  &.critical {
    color: var(--text-color-white);
    border-color: var(--border-color-strong-error);

    &.filled {
      background-color: var(--background-color-strong-error-muted);
    }

    &.closeable:hover {
      background-color: var(--background-color-strong-error-hover);
    }
  }

  &.warning {
    color: var(--text-color-warning);
    border-color: var(--border-color-warning);

    &.filled {
      background-color: var(--background-color-warning);
    }

    &.closeable:hover {
      background-color: var(--background-color-warning-hover);
    }
  }

  &.info {
    color: var(--text-color-info);
    border-color: var(--border-color-info);

    &.filled {
      background-color: var(--background-color-info);
    }

    &.closeable:hover {
      background-color: var(--background-color-info-hover);
    }
  }

  &.community {
    color: var(--text-color-community);
    border-color: var(--border-color-community);

    &.filled {
      background-color: var(--background-color-community);
    }

    &.closeable:hover {
      background-color: var(--background-color-community-hover);
    }
  }

  /* There are no data hover tokens, so hover uses the stronger data background. */
  &.data-highest {
    color: var(--text-color-data-highest);
    border-color: var(--border-color-data-highest-muted);

    &.filled {
      background-color: var(--background-color-data-highest-muted);
    }

    &.closeable:hover {
      background-color: var(--background-color-data-highest);
    }
  }

  &.data-high {
    color: var(--text-color-data-high);
    border-color: var(--border-color-data-high-muted);

    &.filled {
      background-color: var(--background-color-data-high-muted);
    }

    &.closeable:hover {
      background-color: var(--background-color-data-high);
    }
  }

  &.data-medium {
    color: var(--text-color-data-medium);
    border-color: var(--border-color-data-medium-muted);

    &.filled {
      background-color: var(--background-color-data-medium-muted);
    }

    &.closeable:hover {
      background-color: var(--background-color-data-medium);
    }
  }

  &.data-low {
    color: var(--text-color-data-low);
    border-color: var(--border-color-data-low-muted);

    &.filled {
      background-color: var(--background-color-data-low-muted);
    }

    &.closeable:hover {
      background-color: var(--background-color-data-low);
    }
  }
}
</style>
